import { Tooltip, Typography } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import { formatEther } from 'ethers/lib/utils';
import moment from 'moment';
import type { FC } from 'react';
import { useContext, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';

import { ReactComponent as EthIcon } from '../../../common/svg/sidebar/header/icon-eth-small.svg';
import { ReactComponent as StarsIcon } from '../../../common/svg/sidebar/header/icon-stars-small.svg';
import { ModalStateContext } from '../../../contexts/app-state-context/modal-state.context';
import { useWeb3State } from '../../../hooks';
import { useMarketplaceContract } from '../../../hooks/marketplace-contract.hooks';
import {
  getIsEthListing,
  getIsStarsListing,
  getListingEthPrice,
  getListingStarsPrice,
} from '../../../lib/graph/graph-utils';
import type { Listing } from '../../../lib/graph/MogulMarketplace';
import { AssetType, ListingType } from '../../../lib/graph/MogulMarketplace';
import {
  getEthListingTokenPrice,
  getNetworkCurrencySymbol,
  getStarsListingTokenPrice,
} from '../../../lib/smart-contract-utils';
import { ConnectWalletModal } from '../../common/modal';
import { BidAuctionModal } from '../../common/modal/sell-auction-nft/BidAuctionModal.component';
import { CancelNftListingModal } from '../../common/modal/sell-auction-nft/CancelNftListingModal.component';
import { ClaimNftAuctionModal } from '../../common/modal/sell-auction-nft/ClaimNftAuctionModal.component';
import { ModifyNFTListingPriceModal } from '../../common/modal/sell-auction-nft/ModifyNFTListingPriceModal.component';
import { PurchaseNFTModal } from '../../common/modal/sell-auction-nft/PurchaseNFTModal.component';
import { Button } from '../../core/button';
import styles from './NftListingRow.module.css';

interface NftListingRowProps extends Listing {
  isWhitelisted?: boolean;
  isWhitelistAddress?: boolean;
}

export const NftListingRowComponent: FC<NftListingRowProps> = props => {
  const {
    listingId,
    type,
    asset,
    quantity,
    startTime,
    endTime,
    // starsPrice,
    // ethPrice,
    // isStarsListing,
    // isEthListing,
    seller,
    highestBidder,
    highestBidAmount,
    isWhitelisted = false,
    isWhitelistAddress = false,
  } = props;
  const isStarsListing = useMemo(() => getIsStarsListing(props), [props]);
  const isEthListing = useMemo(() => getIsEthListing(props), [props]);
  const listingStarsPrice = useMemo(() => getListingStarsPrice(props), [props]);
  const listingEthPrice = useMemo(() => getListingEthPrice(props), [props]);
  const { walletDetails } = useWeb3State();
  const { setModal } = useContext(ModalStateContext);
  const marketplaceContract = useMarketplaceContract({
    type: asset.type ?? AssetType.Erc1155,
  });

  const [conPrice, setConPrice] = useState<string>();

  useEffect(() => {
    if (!asset.type && !marketplaceContract.initialized) {
      return;
    }
    void (async () => {
      if (type === ListingType.Listing) {
        const listing = await marketplaceContract.getListingById(
          listingId as string,
        );

        if (!listing) {
          return;
        }

        if (isEthListing) {
          const listingPriceEth = getEthListingTokenPrice(
            listing.listingTokens,
            listing.listingPrices,
          );
          setConPrice(listingPriceEth.toString());
        } else {
          const listingPriceStars = getStarsListingTokenPrice(
            listing.listingTokens,
            listing.listingPrices,
          );
          setConPrice(listingPriceStars?.toString());
        }
      } else {
        const auction = await marketplaceContract.getAuctionById(
          listingId as string,
        );

        const highestBid = auction?.highestBid.amount.toString();
        const startingPrice = auction?.startingPrice.toString();
        const startingBidPrice =
          highestBid !== undefined && highestBid !== '0'
            ? highestBid
            : startingPrice ?? '0';

        setConPrice(startingBidPrice);
      }
    })();
  }, [marketplaceContract, asset]);

  const purchaseNftOnClick = () => {
    if (!walletDetails.address) {
      setModal(ConnectWalletModal());
    } else {
      if (isWhitelisted && !isWhitelistAddress) {
        toast.error('Wallet address is not eligible for whitelist sale');
        return;
      }
      setModal(
        PurchaseNFTModal({ isWhitelisted, isWhitelistAddress, listing: props }),
      );
    }
  };
  const placeBidForNftOnClick = () => {
    if (!walletDetails.address) {
      setModal(ConnectWalletModal());
    } else {
      if (isWhitelisted && !isWhitelistAddress) {
        toast.error('Wallet address is not eligible for whitelist sale');
        return;
      }
      setModal(
        BidAuctionModal({ isWhitelisted, isWhitelistAddress, listing: props }),
      );
    }
  };
  const cancelListingOnClick = () => {
    setModal(CancelNftListingModal({ listing: props }));
  };
  const claimAuctionOnClick = () => {
    setModal(ClaimNftAuctionModal({ listing: props }));
  };
  const modifyPriceOnClick = () => {
    setModal(ModifyNFTListingPriceModal({ listing: props }));
  };

  const currentTime = Math.floor(Date.now() / 1000);
  const endDateFromNow = moment(endTime * 1000).fromNow();
  const startDateFromNow = moment(startTime * 1000).fromNow();

  const getFormattedDate = () => {
    let formattedDate = '';

    if (currentTime < startTime) {
      formattedDate = 'Starts ' + startDateFromNow;
    } else if (currentTime > startTime && currentTime < endTime) {
      formattedDate = 'Ends ' + endDateFromNow;
    } else {
      formattedDate = 'Ended ' + endDateFromNow;
    }

    return formattedDate;
  };

  if (asset.type === null) {
    return <Skeleton height={75} />;
  }

  return (
    <div
      className={`${styles.nftListingRowContainer} xl:flex flex-col lg:flex-row`}
    >
      <div>
        <Typography
          variant="body2"
          style={{
            fontSize: 16,
            marginRight: '0.5em',
          }}
        >
          {type === ListingType.Auction ? 'Auction' : 'Listing'}
        </Typography>
      </div>
      {((isEthListing && !isStarsListing) ||
        (isEthListing && isStarsListing)) && (
        <div className="flex">
          <div className="flex items-center mr-3">
            <div
              className={`flex justify-center items-center h-6 w-6 ${styles.nftListingRowIcon}`}
            >
              <EthIcon />
            </div>
          </div>
          <div className="flex">
            {formatEther(
              conPrice ??
                (highestBidAmount as string | undefined) ??
                (listingEthPrice as string),
            ) + ` ${getNetworkCurrencySymbol()}`}
          </div>
        </div>
      )}
      {isStarsListing && !isEthListing && (
        <div className="flex">
          <div className="flex items-center mr-3">
            <div
              className={`flex justify-center items-center h-6 w-6 ${styles.nftListingRowIcon}`}
            >
              <StarsIcon />
            </div>
          </div>
          <div className="flex">
            {formatEther(
              conPrice ??
                (highestBidAmount as string | undefined) ??
                (listingStarsPrice as string),
            ) + ' MOGUL'}
          </div>
        </div>
      )}
      <div>Quantity: {quantity}</div>
      <div className="flex-col md:flex">
        <Typography style={{ fontSize: 16, opacity: 1 }} className="truncate">
          {(seller.address as string).substring(0, 7)}
        </Typography>
      </div>
      <div className="md:flex">
        <Typography variant="body2" style={{ fontSize: 16 }}>
          {type === ListingType.Auction ? getFormattedDate() : '--'}
        </Typography>
      </div>
      {seller.address !== walletDetails.address?.toLowerCase() && (
        <div className="flex justify-start xl:justify-around xl:px-6">
          <Tooltip
            disableHoverListener={
              type === ListingType.Listing ||
              (startTime < currentTime && endTime > currentTime)
            }
            title="Auction not active"
            placement="top"
            arrow
          >
            <span>
              <Button
                style={
                  type === ListingType.Auction &&
                  (startTime > currentTime || endTime < currentTime)
                    ? { pointerEvents: 'none' }
                    : {}
                }
                onClick={() => {
                  if (type === ListingType.Listing) {
                    purchaseNftOnClick();
                  } else {
                    placeBidForNftOnClick();
                  }
                }}
                className={`${styles.buyButton} flex items-center mt-2 xl:mt-0 px-5`}
                disabled={
                  (type === ListingType.Auction &&
                    (startTime > currentTime || endTime < currentTime)) ||
                  (Boolean(walletDetails.address) &&
                    (isWhitelisted ? !isWhitelistAddress : false))
                }
              >
                {type === ListingType.Auction ? 'Bid' : 'Buy'}
              </Button>
            </span>
          </Tooltip>
        </div>
      )}
      {(seller.address === walletDetails.address?.toLowerCase() ||
        highestBidder?.address === walletDetails.address?.toLowerCase()) && (
        <div className="flex flex-row">
          {seller.address === walletDetails.address?.toLowerCase() &&
            (type === ListingType.Listing || endTime > currentTime) && (
              <Button
                kind="secondary"
                onClick={cancelListingOnClick}
                className="flex items-center mt-2 xl:mt-0 mr-1"
              >
                Cancel
              </Button>
            )}
          {seller.address === walletDetails.address?.toLowerCase() &&
            type === ListingType.Listing && (
              <Button
                onClick={modifyPriceOnClick}
                className="flex items-center mt-2 xl:mt-0"
              >
                Modify
              </Button>
            )}
          {type === ListingType.Auction && endTime < currentTime && (
            <Button
              onClick={claimAuctionOnClick}
              className="flex items-center mt-2 xl:mt-0"
            >
              {highestBidder?.address === seller.address ? 'Cancel' : 'Claim'}
            </Button>
          )}
        </div>
      )}
    </div>
  );
};
